import { useCallback } from 'react';
import type { ReadonlyDeep } from 'type-fest';

import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { resolvableServiceUsageForm2ApiMapper } from '../../ResolvableServiceUsage/mappers/resolvable-service-usage.form2api.mapper';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';

import type { SubmitHandler } from '../types/submit-handler.type';
import type { UpdateMutation } from '../types/props.type';

import { useFindResolvableServiceUsageIndexById } from './useFindIndexById.hook';

interface Params {
  readonly directMutation?: UpdateMutation;
  readonly formContext?: string;
  readonly handleFlushOneSuccess: () => void;
  readonly serviceUsageId?: Id | null;
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

interface FormikArrayActions {
  readonly flushOne: (
    index: number,
  ) => ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>;
  readonly updateInForm: (
    index: number,
    newValue: ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>,
  ) => void;
}

export const useFlushOneResolvableServiceUsageSubmit = ({
  directMutation,
  formContext,
  handleFlushOneSuccess,
  serviceUsageId: pServiceUsageId,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const findIndexById = useFindResolvableServiceUsageIndexById({ formContext });

  const handleSubmitFactory = useCallback(
    ({
      flushOne,
      updateInForm,
    }: Readonly<FormikArrayActions>): SubmitHandler<ResolvableServiceUsageForm_ResolvableServiceUsage> =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        const { serviceUsageId } = ensureDefined({
          serviceUsageId: pServiceUsageId,
        });

        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const index = findIndexById(serviceUsageId);

        if (index === -1) {
          setFormError(["La prestation n'existe pas"]);
          setSubmitting(false);
          return undefined;
        }

        const newValue = flushOne(index);

        const onSuccess = () => {
          updateInForm(index, newValue);

          handleFlushOneSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              serviceUsageId,
              serviceUsagesGroupId,
              serviceUserId,
              update: resolvableServiceUsageForm2ApiMapper(newValue),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      directMutation,
      findIndexById,
      serviceUsagesGroupId,
      serviceUserId,
      handleFlushOneSuccess,
      pServiceUsageId,
    ],
  );

  return { handleSubmitFactory };
};
